import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Check as YesIcon,
  Close as NoIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';

/**
 * Table Grid Step Component
 *
 * Allows users to fill in a configurable table with different input types per column
 *
 * Options Config:
 * {
 *   "columns": [
 *     {
 *       "id": "col1",
 *       "label": "Item",
 *       "type": "text"  // text, number, yes_no, select
 *     },
 *     {
 *       "id": "col2",
 *       "label": "Status",
 *       "type": "yes_no"
 *     }
 *   ],
 *   "rows": [
 *     { "__rowName": "Row 1", "__defaults": { "col1": "Item 1", "col2": "yes" } },
 *     { "__rowName": "Row 2", "__defaults": { "col1": "Item 2", "col2": "no" } }
 *   ]
 * }
 *
 * Nothing is saved until the user edits a cell; the first edit saves the whole
 * table, defaults included.
 */

// Columns that are answers (as opposed to free-text remarks), used for progress
const ANSWER_TYPES = ['yes_no', 'select', 'number'];

function buildRows(rows, value) {
  const hasValue = Array.isArray(value) && value.length > 0;
  const base = hasValue ? value : rows.map(row => ({
    __rowName: row.__rowName || '',
    __data: { ...row.__defaults },
  }));

  return base.map((rowData, idx) => {
    const rowTemplate = rows[idx] || {};
    return {
      __rowName: rowData?.__rowName || rowTemplate.__rowName || '',
      __data: { ...(rowTemplate.__defaults || {}), ...(rowData?.__data || {}) },
    };
  });
}

/**
 * How many answer cells (yes/no, select, number) are filled in, for the
 * "x af y udfyldt" hint on the step.
 */
export function tableGridProgress(step, value) {
  const options = step.options || {};
  const columns = (options.columns || []).filter(c => ANSWER_TYPES.includes(c.type));
  const rows = buildRows(options.rows || [], value);
  const total = columns.length * rows.length;
  const filled = rows.reduce(
    (sum, row) => sum + columns.filter(c => `${row.__data[c.id] ?? ''}` !== '').length,
    0,
  );
  return { filled, total };
}

const cellBorder = { border: '1px solid', borderColor: 'divider' };

export default function TableGridStep({ step, value, onChange, disabled, preview }) {
  const { t } = useTranslation(['procedures']);
  const options = step.options || {};
  const columns = useMemo(() => options.columns || [], [options.columns]);
  const rows = useMemo(() => options.rows || [], [options.rows]);
  const readOnly = disabled || preview;

  const displayValue = useMemo(() => buildRows(rows, value), [value, rows]);

  const handleCellChange = useCallback((rowIndex, colId, newValue) => {
    const updatedValue = displayValue.map((rowData, idx) => (
      idx === rowIndex
        ? { ...rowData, __data: { ...rowData.__data, [colId]: newValue } }
        : rowData
    ));
    onChange(updatedValue);
  }, [displayValue, onChange]);

  // Most rounds find nothing wrong: answer the untouched yes/no cells in one go
  const yesNoColumns = columns.filter(c => c.type === 'yes_no');
  const emptyYesNoCells = displayValue.reduce(
    (sum, row) => sum + yesNoColumns.filter(c => !row.__data[c.id]).length,
    0,
  );
  const handleFillRemainingYes = () => {
    onChange(displayValue.map(row => ({
      ...row,
      __data: {
        ...row.__data,
        ...Object.fromEntries(yesNoColumns.filter(c => !row.__data[c.id]).map(c => [c.id, 'yes'])),
      },
    })));
  };

  const renderCellInput = (rowIndex, column) => {
    const cellValue = displayValue?.[rowIndex]?.__data?.[column.id] ?? '';

    switch (column.type) {
      case 'text':
        return (
          <TextField
            size="small"
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
            disabled={readOnly}
            fullWidth
            multiline
            maxRows={4}
            variant="outlined"
            sx={{ minWidth: 160 }}
          />
        );

      case 'number':
        return (
          <TextField
            size="small"
            type="number"
            value={cellValue}
            onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
            disabled={readOnly}
            fullWidth
            variant="outlined"
            sx={{ minWidth: 90 }}
          />
        );

      case 'yes_no':
        return (
          <ToggleButtonGroup
            value={cellValue || null}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                handleCellChange(rowIndex, column.id, newValue);
              }
            }}
            disabled={readOnly}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.5 } }}
          >
            <ToggleButton
              value="yes"
              aria-label={t('procedures:table_grid.yes', 'Ja')}
              sx={{
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: 'success.main',
                  color: 'success.contrastText',
                },
              }}
            >
              <YesIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton
              value="no"
              aria-label={t('procedures:table_grid.no', 'Nej')}
              sx={{
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: 'error.main',
                  color: 'error.contrastText',
                },
              }}
            >
              <NoIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        );

      case 'select':
        return (
          <FormControl size="small" fullWidth sx={{ minWidth: 120 }}>
            <Select
              value={cellValue}
              onChange={(e) => handleCellChange(rowIndex, column.id, e.target.value)}
              disabled={readOnly}
              displayEmpty
            >
              <MenuItem value="">{t('procedures:table_grid.select', 'Vælg...')}</MenuItem>
              {(column.options || []).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return <Typography variant="body2">{cellValue || '-'}</Typography>;
    }
  };

  if (columns.length === 0 || rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        {t('procedures:table_grid.no_config', 'Tabellen er ikke sat op')}
      </Typography>
    );
  }

  return (
    <Box>
      {!readOnly && emptyYesNoCells > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button size="small" startIcon={<DoneAllIcon />} onClick={handleFillRemainingYes}>
            {t('procedures:table_grid.fill_remaining_yes', {
              count: emptyYesNoCells,
              defaultValue: 'Sæt resterende til Ja ({{count}})',
            })}
          </Button>
        </Box>
      )}
      {/* The table scrolls inside its own box, never the page */}
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', borderRadius: 1, ...cellBorder }}>
        <Table size="small" sx={{ borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...cellBorder, bgcolor: 'background.default', position: 'sticky', left: 0, zIndex: 2 }} />
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    ...cellBorder,
                    bgcolor: 'background.default',
                    color: 'text.primary',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    whiteSpace: 'normal',
                    textAlign: column.type === 'text' ? 'left' : 'center',
                    verticalAlign: 'bottom',
                    minWidth: column.type === 'yes_no' ? 96 : undefined,
                    maxWidth: column.type === 'yes_no' ? 140 : undefined,
                    px: 1,
                    py: 1,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIndex) => {
              const rowData = displayValue[rowIndex]?.__data || {};
              const hasNo = columns.some(c => c.type === 'yes_no' && rowData[c.id] === 'no');
              return (
                <TableRow key={rowIndex}>
                  <TableCell
                    sx={{
                      ...cellBorder,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      px: 1.5,
                      py: 1,
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      bgcolor: 'background.paper',
                      // Flag rows with a "no" so the remark column gets filled in
                      boxShadow: hasNo ? (theme) => `inset 3px 0 0 ${theme.palette.error.main}` : 'none',
                    }}
                  >
                    {row.__rowName || '-'}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell
                      key={`${rowIndex}-${column.id}`}
                      sx={{
                        ...cellBorder,
                        px: 1,
                        py: 0.75,
                        textAlign: column.type === 'text' ? 'left' : 'center',
                        width: column.type === 'text' ? '40%' : undefined,
                      }}
                    >
                      {renderCellInput(rowIndex, column)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
